<template>
  <sp-card class="sp-info-card" :class="classModifiers" part="root" :style="style">
    <a
      v-if="href"
      slot="content-overlay"
      class="sp-info-card__link-overlay"
      :href="href"
      :target="target"
      :aria-label="ariaLabel"
      part="link"
    ></a>

    <div class="sp-info-card__header">
      <div v-if="icon" class="sp-info-card__icon" part="icon">
        <sp-icon :name="icon" :size="size" :size-sm="sizeSm"></sp-icon>
      </div>

      <div class="sp-info-card__text">
        <sp-card-title class="sp-info-card__title" part="title">{{ title }}</sp-card-title>

        <sp-card-content v-if="content" class="sp-info-card__content" part="content">
          <p>{{ content }}</p>
        </sp-card-content>
      </div>
    </div>
  </sp-card>
</template>

<script setup>
import { computed } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useColor } from "#ce/composables/useColor";
import { toBoolean, toCssUnit } from "#ce/utils/props";
import { props as propDefs } from "./props";

const props = defineProps(propDefs);

const { isSmScreen, breakpointModifiers } = useBreakpoints();
const { colorModifiers } = useColor(props);

const effectiveLayout = computed(() => {
  if (isSmScreen.value && props.layoutSm) {
    return props.layoutSm;
  }
  return props.layout;
});

const effectiveAlign = computed(() => {
  if (isSmScreen.value && props.alignSm) {
    return props.alignSm;
  }
  return props.align;
});

const isFillHeight = computed(() => toBoolean(props.fillHeight));

const effectiveHeight = computed(() => {
  if (isFillHeight.value) {
    return "100%";
  }
  return toCssUnit(props.height);
});

const effectiveHeightSm = computed(() => {
  if (isFillHeight.value) {
    return "100%";
  }
  return toCssUnit(props.heightSm);
});

const style = computed(() => ({
  "--min-height": toCssUnit(props.minHeight),
  "--min-height-sm": toCssUnit(props.minHeightSm),
  "--height": effectiveHeight.value,
  "--height-sm": effectiveHeightSm.value,
}));

const classModifiers = computed(() => [
  ...colorModifiers.value,
  ...breakpointModifiers.value,
  `--layout-${effectiveLayout.value}`,
  `--align-${effectiveAlign.value}`,
  `--variant-${props.variant}`,
]);

const ariaLabel = computed(() => (props.href ? props.title : undefined));
</script>

<style lang="scss">
@import "./shared/info-card-theme";

:host {
  /* Layout and sizing */
  --gap: var(--sp-comp-info-card-gap, var(--sp-ref-scale-12));
  --gap-sm: var(--sp-comp-info-card-gap-sm, var(--sp-ref-scale-8));
  --background: var(--sp-comp-info-card-background, var(--sp-sys-color-surface));
  --border-radius: var(--sp-comp-info-card-border-radius, var(--sp-ref-border-radius-md));
  --border: var(--sp-comp-info-card-border-width, 0) var(--sp-comp-info-card-border-style, solid)
    var(--sp-comp-info-card-border-color, var(--sp-sys-color-white));
  --padding-block: var(--sp-comp-info-card-padding-block, var(--sp-ref-scale-32));
  --padding-inline: var(--sp-comp-info-card-padding-inline, var(--sp-ref-scale-24));
  --padding-block-sm: var(--sp-comp-info-card-padding-block-sm, var(--sp-ref-scale-24));
  --padding-inline-sm: var(--sp-comp-info-card-padding-inline-sm, var(--sp-ref-scale-24));
  --box-shadow: var(--sp-comp-info-card-box-shadow, var(--sp-sys-elevation-3));

  /* Typography */
  --title-color: var(--sp-comp-info-card-title-color, var(--sp-sys-color-primary));
  --title-font-family: var(--sp-comp-info-card-title-font-family, var(--sp-sys-typescale-h3-font-family));
  --title-font-size: var(--sp-comp-info-card-title-font-size, var(--sp-sys-typescale-h3-font-size));
  --title-font-size-sm: var(--sp-comp-info-card-title-font-size-sm, var(--sp-sys-typescale-h5-font-size));
  --title-font-weight: var(--sp-comp-info-card-title-font-weight, var(--sp-sys-font-weight-bold));
  --title-line-height: var(--sp-comp-info-card-title-line-height, var(--sp-sys-typescale-h3-line-height));
  --title-line-height-sm: var(--sp-comp-info-card-title-line-height-sm, var(--sp-sys-typescale-h5-line-height));

  --content-color: var(--sp-comp-info-card-content-color, var(--sp-sys-color-on-surface));
  --content-font-family: var(--sp-comp-info-card-content-font-family, var(--sp-sys-typescale-body-font-family));
  --content-font-size: var(--sp-comp-info-card-content-font-size, var(--sp-ref-font-size-large, 1.25rem));
  --content-font-size-sm: var(--sp-comp-info-card-content-font-size-sm, var(--sp-ref-font-size-medium, 1rem));
  --content-line-height: var(--sp-comp-info-card-content-line-height, var(--sp-ref-scale-32));
  --content-line-height-sm: var(--sp-comp-info-card-content-line-height-sm, var(--sp-ref-scale-32));

  /* Icon */
  --icon-fill: var(--sp-comp-info-card-icon-fill, var(--sp-sys-color-outline));
  --icon-size: var(--sp-comp-info-card-icon-size, var(--sp-sys-icon-size-xx-large));

  display: block;
  height: var(--sp-info-card-root-height, auto);
}

:host([fill-height]),
:host([height$="%"]) {
  --sp-info-card-root-height: 100%;
}
</style>

<style scoped lang="scss">
.sp-info-card {
  border: var(--border);
  border-radius: var(--sp-info-card-group-border-radius, var(--border-radius));
  box-shadow: var(--box-shadow);
  position: relative;
  background: var(--background);

  box-sizing: border-box;
  padding-block: var(--sp-info-card-group-padding-block, var(--padding-block));
  padding-inline: var(--sp-info-card-group-padding-inline, var(--padding-inline));

  min-height: var(--sp-info-card-group-min-height, var(--min-height, 0px));

  height: var(--sp-comp-info-card-height, var(--height, auto));

  &.--is-sm-screen {
    padding-block: var(--sp-info-card-group-padding-block-sm, var(--padding-block-sm));
    padding-inline: var(--sp-info-card-group-padding-inline-sm, var(--padding-inline-sm));
    min-height: var(--sp-info-card-group-min-height-sm, var(--min-height-sm, 0px));
    height: var(--sp-comp-info-card-height-sm, var(--height-sm, var(--height, auto)));
  }

  @each $v in default, neutral, inverted {
    &.--variant-#{$v} {
      --background: var(--#{$v}-background);

      --gap: var(--#{$v}-gap);
      --gap-sm: var(--#{$v}-gap-sm);
      --title-content-gap: var(--#{$v}-title-content-gap);
      --title-content-gap-sm: var(--#{$v}-title-content-gap-sm);

      --padding-block: var(--#{$v}-padding-block);
      --padding-inline: var(--#{$v}-padding-inline);
      --padding-block-sm: var(--#{$v}-padding-block-sm);
      --padding-inline-sm: var(--#{$v}-padding-inline-sm);

      --border-radius: var(--#{$v}-border-radius);
      --min-height: var(--#{$v}-min-height);
      --min-height-sm: var(--#{$v}-min-height-sm);

      --icon-fill: var(--#{$v}-icon-fill);

      /* Title */
      --title-color: var(--#{$v}-title-color);
      --title-font-family: var(--#{$v}-title-font-family);
      --title-font-weight: var(--#{$v}-title-font-weight);
      --title-font-size: var(--#{$v}-title-font-size);
      --title-line-height: var(--#{$v}-title-line-height);
      --title-font-size-sm: var(--#{$v}-title-font-size-sm);
      --title-line-height-sm: var(--#{$v}-title-line-height-sm);

      /* Content */
      --content-color: var(--#{$v}-content-color);
      --content-font-family: var(--#{$v}-content-font-family);
      --content-font-size: var(--#{$v}-content-font-size);
      --content-line-height: var(--#{$v}-content-line-height);
      --content-font-size-sm: var(--#{$v}-content-font-size-sm);
      --content-line-height-sm: var(--#{$v}-content-line-height-sm);

      /* Icon */
      --icon-size: var(--#{$v}-icon-size);
      &.--is-sm-screen {
        --icon-size: var(--#{$v}-icon-size-sm);
      }
    }
  }

  @each $c in primary, secondary, danger, text, error, warning, success, info, "white", custom {
    &.--variant-inverted.--color-#{$c} {
      --background: var(--sp-sys-color-#{$c});
    }
  }

  :host([href]:hover) &,
  :host([data-behaviour~="open-form-modal"]:hover) & {
    background: var(--sp-comp-info-card-background-hover, var(--background));
  }

  &.--align-start {
    --header-align: flex-start;
    --text-align: left;
  }
  &.--align-center {
    --header-align: center;
    --text-align: center;
  }
  &.--align-end {
    --header-align: flex-end;
    --text-align: right;
  }

  &.--layout-column {
    --header-direction: column;
  }
  &.--layout-column-reverse {
    --header-direction: column-reverse;
  }
  &.--layout-row {
    --header-direction: row;
  }
  &.--layout-row-reverse {
    --header-direction: row-reverse;
  }

  // Row layouts force left text alignment (icon is beside text, not above)
  &.--layout-row,
  &.--layout-row-reverse {
    --text-align: left;
  }

  &__header {
    display: flex;
    gap: var(--gap);
    flex-direction: var(--sp-info-card-group-header-direction, var(--header-direction, column));
    align-items: var(--sp-info-card-group-header-align, var(--header-align, flex-start));

    .--is-sm-screen & {
      gap: var(--gap-sm);
    }
  }

  &__icon {
    flex-shrink: 0;
    order: var(--icon-order, 0);

    sp-icon {
      --fill: var(--icon-fill);
      --size: var(--icon-size);
    }
  }

  &__text {
    min-width: 0;
    order: var(--text-order, 1);
  }

  &__title {
    color: var(--title-color);
    font-family: var(--title-font-family);
    font-size: var(--title-font-size);
    font-weight: var(--title-font-weight);
    line-height: var(--title-line-height);
    text-align: var(--sp-info-card-group-text-align, var(--text-align, left));

    .--is-sm-screen & {
      font-size: var(--title-font-size-sm);
      line-height: var(--title-line-height-sm);
    }
  }

  &__content,
  &__content p {
    color: var(--content-color);
    font-family: var(--content-font-family);
    font-size: var(--content-font-size);
    line-height: var(--content-line-height);
    text-align: var(--sp-info-card-group-text-align, var(--text-align, left));
    margin-top: var(--title-content-gap, var(--gap));
    margin-bottom: 0;
    padding: 0;

    .--is-sm-screen & {
      font-size: var(--content-font-size-sm);
      line-height: var(--content-line-height-sm);
      margin-top: var(--title-content-gap-sm, var(--gap-sm));
    }
  }

  &__link-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: inherit;
    cursor: pointer;
  }
}
</style>
